import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { VehicleLog, VehicleLogFilters } from "./types";
import { campusApi } from "../campus/campus-api";
import { Campus } from "../campus/types";

const VehicleLogsLive: React.FC = () => {
  const [logs, setLogs] = useState<VehicleLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<VehicleLog[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [filters, setFilters] = useState<VehicleLogFilters>({
    status: "all",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch campuses on mount
  useEffect(() => {
    fetchCampuses();
  }, []);

  // WebSocket connection
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        // wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...logs];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.number_plate?.toLowerCase().includes(query) ||
          log.reg_no?.toLowerCase().includes(query) ||
          log.owner?.name?.toLowerCase().includes(query) ||
          log.member?.name?.toLowerCase().includes(query) ||
          log.campus?.name?.toLowerCase().includes(query) ||
          log.entry_node?.node_name?.toLowerCase().includes(query) ||
          log.exit_node?.node_name?.toLowerCase().includes(query)
      );
    }

    // Vehicle type filter
    if (filters.vehicle_type) {
      filtered = filtered.filter((log) => log.vehicle_type === filters.vehicle_type);
    }

    // Make filter
    if (filters.make) {
      filtered = filtered.filter((log) => log.make === filters.make);
    }

    // Date filter
    if (filters.date_from) {
      filtered = filtered.filter((log) => new Date(log.timestamp||"") >= new Date(filters.date_from!));
    }
    if (filters.date_to) {
      filtered = filtered.filter((log) => new Date(log.timestamp||"") <= new Date(filters.date_to!));
    }

    // Inside campus filter
    if (filters.is_inside !== undefined) {
      filtered = filtered.filter((log) => log.is_inside === filters.is_inside);
    }

    // Status filter
    if (filters.status && filters.status !== "all") {
      filtered = filtered.filter((log) => log.status === filters.status);
    }

    // Campus filter
    if (filters.campus_id) {
      filtered = filtered.filter((log) => log.campus?.id === filters.campus_id);
    }

    // Owner/Member filter
    if (filters.owner_name) {
      filtered = filtered.filter((log) =>
        log.owner?.name?.toLowerCase().includes(filters.owner_name!.toLowerCase())
      );
    }
    if (filters.member_name) {
      filtered = filtered.filter((log) =>
        log.member?.name?.toLowerCase().includes(filters.member_name!.toLowerCase())
      );
    }

    setFilteredLogs(filtered.splice(0, 10));
  }, [logs, filters, searchQuery]);

  const fetchCampuses = async () => {
    try {
      const data = await campusApi.getAllCampuses();
      setCampuses(data);
    } catch (error) {
      console.error("Failed to load campuses");
    }
  };

  // Transform entry_log data to VehicleLog format
  const transformEntryLog = async (entryLog: any): Promise<VehicleLog> => {
    const log: VehicleLog = {
      id: entryLog.vehicle_log_id || Date.now(),
      vehicle_log_id: String(entryLog.vehicle_log_id),
      number_plate: entryLog.number_plate || entryLog.vehicle?.reg_no || "",
      reg_no: entryLog.vehicle?.reg_no || entryLog.number_plate,
      vehicle_type: entryLog.vehicle_details?.vehicle_type || entryLog.vehicle?.type || null,
      make: entryLog.vehicle_details?.make || null,
      color: entryLog.vehicle_details?.primary_color || null,
      date: entryLog.entry?.time || entryLog.exit?.time || new Date().toISOString(),
      timestamp: entryLog.entry?.time || entryLog.exit?.time || new Date().toISOString(),
      is_inside: entryLog.status === "entry" && entryLog.exit === null,
      status: entryLog.status || (entryLog.exit ? "exit" : "entry"),
      entry_time: entryLog.entry?.time,
      exit_time: entryLog.exit?.time,
      cam_id: entryLog.entry?.camera_id || entryLog.exit?.camera_id,
    };

    // Fetch campus details if campus_id is provided
    if (entryLog.entry?.campus_id) {
      try {
        const campus = await campusApi.getCampusById(entryLog.entry.campus_id);
        log.campus = {
          id: campus.id,
          name: campus.name,
          location: campus.location,
        };
      } catch (error) {
        console.error("Failed to fetch campus:", error);
      }
    }

    // Fetch entry node details if node_id is provided
    if (entryLog.entry?.node_id) {
      try {
        const node = await campusApi.getNodeById(entryLog.entry.node_id);
        log.entry_node = {
          id: node.id,
          node_id: node.node_id,
          node_name: node.node_name,
        };
        log.entry_camera = {
          id: 0,
          node_id: node.node_id,
          node_name: node.node_name,
          camera_id: entryLog.entry.camera_id,
          type: "entry",
        };
      } catch (error) {
        console.error("Failed to fetch entry node:", error);
        // Still set basic info even if fetch fails
        log.entry_node = {
          id: 0,
          node_id: entryLog.entry.node_id,
          node_name: entryLog.entry.node_id.substring(0, 8) + "...",
        };
        log.entry_camera = {
          id: 0,
          node_id: entryLog.entry.node_id,
          node_name: entryLog.entry.node_id.substring(0, 8) + "...",
          camera_id: entryLog.entry.camera_id,
          type: "entry",
        };
      }
    }

    // Fetch exit node details if node_id is provided
    if (entryLog.exit?.node_id) {
      try {
        const node = await campusApi.getNodeById(entryLog.exit.node_id);
        log.exit_node = {
          id: node.id,
          node_id: node.node_id,
          node_name: node.node_name,
        };
        log.exit_camera = {
          id: 0,
          cam_id: entryLog.exit.camera_id,
          type: "exit",
        };
      } catch (error) {
        console.error("Failed to fetch exit node:", error);
        // Still set basic info even if fetch fails
        log.exit_node = {
          id: 0,
          node_id: entryLog.exit.node_id,
          node_name: entryLog.exit.node_id.substring(0, 8) + "...",
        };
        log.exit_camera = {
          id: 0,
          cam_id: entryLog.exit.camera_id,
          type: "exit",
        };
      }
    }

    return log;
  };

  const connectWebSocket = () => {
    try {      

      if (wsRef.current) return

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = window.location.host;
      const wsUrl = `${protocol}//localhost:3070/api/ws/vehicle-logs?access_token=${localStorage.getItem("token")}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {

        setIsConnected(true);
        toast.success("Connected to vehicle logs stream");
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle initial message with entry_logs array
          if (data.type === "vehicle:logs:initial" && Array.isArray(data.entry_logs)) {
            console.log(`Received initial message with ${data.entry_logs.length} logs`);
            
            // Transform all entry logs
            const transformedLogs = await Promise.all(
              data.entry_logs.map((entryLog: any) => transformEntryLog(entryLog))
            );

            // Add all logs to state (replace existing logs for initial load)
            setLogs(transformedLogs);
            toast.success(`Loaded ${transformedLogs.length} vehicle logs`);
            return;
          }

          // Handle regular single log message
          const log: VehicleLog = {
            id: data.vehicle_log_id || data.id || Date.now(),
            vehicle_log_id: String(data.vehicle_log_id || data.id),
            number_plate: data.number_plate || data.vehicle?.reg_no || "",
            reg_no: data.vehicle?.reg_no || data.reg_no || data.number_plate,
            vehicle_type: data.vehicle_details?.vehicle_type || data.vehicle?.type || data.vehicle_type || data.type,
            make: data.vehicle_details?.make || data.make,
            color: data.vehicle_details?.primary_color || data.color,
            date: data.entry?.time || data.exit?.time || data.date || data.timestamp || new Date().toISOString(),
            timestamp: data.entry?.time || data.exit?.time || data.timestamp || data.date || new Date().toISOString(),
            is_inside: data.is_inside !== undefined ? data.is_inside : (data.status === "entry" && !data.exit),
            status: data.status || (data.exit ? "exit" : "entry"),
            entry_time: data.entry?.time || data.entry_time,
            exit_time: data.exit?.time || data.exit_time,
            cam_id: data.entry?.camera_id || data.exit?.camera_id || data.cam_id,
            owner: data.owner,
            member: data.member,
            label: data.label,
            node: data.node,
          };

          // Fetch campus if entry has campus_id
          if (data.entry?.campus_id) {
            try {
              const campus = await campusApi.getCampusById(data.entry.campus_id);
              log.campus = {
                id: campus.id,
                name: campus.name,
                location: campus.location,
              };
            } catch (error) {
              console.error("Failed to fetch campus:", error);
            }
          }

          // Fetch entry node if node_id is provided
          if (data.entry?.node_id) {
            try {
              const node = await campusApi.getNodeById(data.entry.node_id);
              log.entry_node = {
                id: node.id,
                node_id: node.node_id,
                node_name: node.node_name,
              };
              log.entry_camera = {
                id: 0,
                node_id: node.node_id,
                node_name: node.node_name,
                camera_id: data.entry.camera_id,
                type: "entry",
              };
            } catch (error) {
              console.error("Failed to fetch entry node:", error);
            }
          }

          // Fetch exit node if node_id is provided
          if (data.exit?.node_id) {
            try {
              const node = await campusApi.getNodeById(data.exit.node_id);
              log.exit_node = {
                id: node.id,
                node_id: node.node_id,
                node_name: node.node_name,
              };
              log.exit_camera = {
                id: 0,
                cam_id: data.exit.camera_id,
                type: "exit",
              };
            } catch (error) {
              console.error("Failed to fetch exit node:", error);
            }
          }

          console.log("New log:", log.id, log.number_plate);
          setLogs((prev) => [log, ...prev].slice(0, 1000)); // Keep last 1000 logs
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        console.log(error)
        setIsConnected(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
      setIsConnected(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    setFilteredLogs([]);
  };

  const getUniqueValues = (key: keyof VehicleLog) => {
    const values = new Set<string>();
    logs.forEach((log) => {
      const value = log[key];
      if (value && typeof value === "string") {
        values.add(value);
      }
    });
    return Array.from(values) //.sort();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Live Vehicle Logs
              </h1>
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${isConnected ? "bg-green-500 animate-pulse" : "bg-red-500"
                    }`}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isConnected ? "Connected" : "Disconnected"}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-500">
                  ({filteredLogs.length} logs)
                </span>
              </div>
            </div>
            <button
              onClick={clearLogs}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              <i className="fa-solid fa-trash"></i>
              Clear Logs
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by plate, owner, campus..."
              />
            </div>

            {/* Vehicle Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Vehicle Type
              </label>
              <select
                value={filters.vehicle_type || ""}
                onChange={(e) =>
                  setFilters({ ...filters, vehicle_type: e.target.value || undefined })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                {getUniqueValues("vehicle_type").map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Make */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Make
              </label>
              <select
                value={filters.make || ""}
                onChange={(e) => setFilters({ ...filters, make: e.target.value || undefined })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Makes</option>
                {getUniqueValues("make").map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status || "all"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    status: e.target.value as "entry" | "exit" | "all",
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="entry">Entry</option>
                <option value="exit">Exit</option>
              </select>
            </div>

            {/* Inside Campus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <select
                value={filters.is_inside === undefined ? "" : filters.is_inside ? "inside" : "outside"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    is_inside: e.target.value === "" ? undefined : e.target.value === "inside",
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="inside">Inside Campus</option>
                <option value="outside">Outside Campus</option>
              </select>
            </div>

            {/* Campus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Campus
              </label>
              <select
                value={filters.campus_id || ""}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    campus_id: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Campuses</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date From
              </label>
              <input
                type="datetime-local"
                value={filters.date_from || ""}
                onChange={(e) =>
                  setFilters({ ...filters, date_from: e.target.value || undefined })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date To
              </label>
              <input
                type="datetime-local"
                value={filters.date_to || ""}
                onChange={(e) =>
                  setFilters({ ...filters, date_to: e.target.value || undefined })
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                setFilters({ status: "all" });
                setSearchQuery("");
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Number Plate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Campus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Entry Node/Camera
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Exit Node/Camera
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Owner/Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {isConnected ? "No logs match the current filters" : "Waiting for connection..."}
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.vehicle_log_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(log.timestamp||"").toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {log.number_plate || log.reg_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div>
                          {log.vehicle_type && <div>{log.vehicle_type}</div>}
                          {log.make && <div className="text-xs">{log.make}</div>}
                          {log.color && <div className="text-xs text-gray-400">{log.color}</div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.campus ? (
                          <div>
                            <div className="font-medium">{log.campus.name}</div>
                            <div className="text-xs">{log.campus.location}</div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.entry_node ? (
                          <div>
                            <div className="font-medium">{log.entry_node.node_name}</div>
                            {log.entry_camera?.camera_id && (
                              <div className="text-xs">Cam: {log.entry_camera.camera_id}</div>
                            )}
                            {log.entry_time && (
                              <div className="text-xs text-gray-400">
                                {new Date(log.entry_time).toLocaleTimeString()}
                              </div>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.exit_node ? (
                          <div>
                            <div className="font-medium">{log.exit_node.node_name}</div>
                            {log.exit_camera?.cam_id && (
                              <div className="text-xs">Cam: {log.exit_camera.cam_id}</div>
                            )}
                            {log.exit_time && (
                              <div className="text-xs text-gray-400">
                                {new Date(log.exit_time).toLocaleTimeString()}
                              </div>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {log.owner ? (
                          <div>
                            <div className="font-medium">{log.owner.name}</div>
                            {log.owner.phone && <div className="text-xs">{log.owner.phone}</div>}
                            <div className="text-xs">
                              {log.owner.is_member ? (
                                <span className="text-green-600">Member</span>
                              ) : (
                                <span className="text-gray-400">Owner</span>
                              )}
                            </div>
                          </div>
                        ) : log.member ? (
                          <div>
                            <div className="font-medium">{log.member.name}</div>
                            {log.member.phone && <div className="text-xs">{log.member.phone}</div>}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${log.status === "entry"
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            }`}
                        >
                          {log.status === "entry" ? "Entry" : "Exit"}
                        </span>
                        {log.is_inside && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Inside
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleLogsLive;
